/**
 * @file lib/numbering-labels.ts
 * @description 編號序列（numbering_sequences）的中文分類說明與格式示例（2026-09-25 客戶回報序46）。
 *
 * 後端 `nextNumber()`（apps/api/src/services/numbering.ts）以 (scope, scopeKey, year) 三元組
 * 分區維護流水號；此處把技術代碼翻譯成管理者看得懂的「編號分類 + 格式示例」，
 * 供「系統設置 → 編號規則 → 編號序列管理」頁面顯示。
 */

/** 驗證方案代碼 → 中文 */
const SCHEME_LABEL: Record<string, string> = {
  TOP: '有機',
  TAP: '產銷履歷',
  ECO: '友善',
};

/** 驗證方案代碼 → 驗證註冊碼前綴 */
const SCHEME_CODE_PREFIX: Record<string, string> = {
  TOP: 'CAS',
  TAP: 'TAP',
  ECO: 'ECO',
};

/** 證書字號規則 → 中文與示例前綴 */
const CERT_DOC_RULES: Record<string, { label: string; example: (yy: string) => string }> = {
  'top-agri': { label: '證書字號（有機 農糧）', example: (yy) => `1-011-0${yy}001` },
  'top-processing': { label: '證書字號（有機 加分流）', example: (yy) => `1-011-1${yy}001` },
  tap: { label: '證書字號（產銷履歷）', example: (yy) => `ZH-${yy}TAP001` },
};

/** 簡單年度流水類編號：scope → [中文分類, 前綴] */
const YEARLY_SCOPES: Record<string, [string, string]> = {
  quotation: ['報價單編號', 'QT'],
  payment: ['費用單編號', 'PAY'],
  decision: ['驗證決定編號', 'DEC'],
  tc: ['交易證明（TC）編號', 'TC'],
  'contract-review': ['合約評審單編號', 'CR'],
};

/** 編號序列的中文描述 */
export interface NumberingSequenceDescription {
  /** 編號分類（中文） */
  category: string;
  /** 下一號的格式示例（以序號 1 示意） */
  example: string;
}

/**
 * 把 (scope, scopeKey, year) 翻譯為中文分類與格式示例。
 *
 * @param scope    後端 scope 代碼（client / case / audit-plan / cert-doc …）
 * @param scopeKey 分區 key（方案、客戶序號、案件號、證書規則…）
 * @param year     年度（0 = 不分年）
 * @returns 中文分類與格式示例；未知 scope 回傳原代碼
 */
export function describeNumberingSequence(
  scope: string,
  scopeKey: string,
  year: number,
): NumberingSequenceDescription {
  const yyyy = year > 0 ? String(year) : String(new Date().getFullYear());
  const yy = yyyy.slice(-2);

  switch (scope) {
    case 'client': {
      const scheme = SCHEME_LABEL[scopeKey] ?? scopeKey;
      const prefix = SCHEME_CODE_PREFIX[scopeKey] ?? scopeKey;
      return { category: `驗證註冊碼（${scheme}，不分年）`, example: `ZH-${prefix}0001` };
    }
    case 'case': {
      // scopeKey = `${scheme}:${客戶流水號}`，每位客戶每年各自從 01 起號
      const [scheme = '', clientSeq = ''] = scopeKey.split(':');
      const prefix = SCHEME_CODE_PREFIX[scheme] ?? scheme;
      const seq = clientSeq.padStart(4, '0');
      return {
        category: `案件編號（${SCHEME_LABEL[scheme] ?? scheme} 客戶 ${prefix}${seq}，${yyyy} 年度）`,
        example: `${yyyy}-${prefix}${seq}-01`,
      };
    }
    case 'audit-plan':
      return {
        category: `稽核計劃編號（${yyyy} 年度）`,
        example: `${yyyy}-{派案日期YYYYMMDD}-01`,
      };
    case 'cert-doc': {
      const rule = CERT_DOC_RULES[scopeKey];
      return rule
        ? { category: `${rule.label}（${yyyy} 年度）`, example: rule.example(yy) }
        : { category: `證書字號（${scopeKey}）`, example: '—' };
    }
    case 'lab-report':
      return { category: `實驗室報告編號（案件 ${scopeKey}）`, example: `LAB-${scopeKey}-01` };
    case 'nc':
      return { category: `不符合事項編號（案件 ${scopeKey}）`, example: `NC-${scopeKey}-01` };
    default: {
      const yearly = YEARLY_SCOPES[scope];
      if (yearly) {
        return { category: `${yearly[0]}（${yyyy} 年度）`, example: `${yearly[1]}-${yyyy}-000001` };
      }
      return { category: scope, example: '—' };
    }
  }
}
